/* React and Next */

import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';
import { useTranslation } from 'next-i18next';

/* Material UI */

import {
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';

/* Icons */

import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import ArrowRightAltIcon from '@material-ui/icons/ArrowRightAlt';

/* Validation */

import { useFormik } from 'formik';
import * as Yup from 'yup';

/* Local imports */

import { useRouter } from 'next/router';
import { useCookies } from 'react-cookie';
import { getUserData, signInEmail } from '@/features/auth/api/slice';
import { RootState } from '@/store/rootReducer';
import { AppDispatch } from '@/store/store';
import Button from '@/features/markets/components/Button';
import Spacer from '@/components/ui-components/Spacer';

/* Style */
const useStyles = makeStyles((theme) => ({
  textField: {
    color: 'black',
  },
  errors: {
    marginTop: theme.spacing(1),
  },
  inputfieldFullWidth: {
    width: '100%',
    marginTop: theme.spacing(1),
  },
  nextButton: {
    marginTop: theme.spacing(1),
  },
}));

/* Interfaces */
interface Props {
  email: string;
}

const defaultProps = {};

const FormPassword: React.FC<Props> = ({ email }: Props) => {
  /* Variables */
  const classes = useStyles();
  const { t } = useTranslation('users');
  const dispatch = useDispatch() as AppDispatch;
  const { push, query } = useRouter();
  const { auth } = useSelector((state: RootState) => state);

  const [showPassword, setShowPassword] = useState(false);

  const [cookies, setCookie] = useCookies();

  /* Validation */
  const formik = useFormik({
    initialValues: {
      password: '',
    },
    validationSchema: Yup.object({
      password: Yup.string().required(t('login.form_errors.required')),
    }),

    onSubmit: (values) => {
      dispatch(signInEmail({ email, password: values.password }))
        .then(unwrapResult)
        .then((res) => {
          if (res.key) {
            // store cookie if we can
            if (cookies.essential) {
              setCookie('token', res.key, {
                path: '/',
                expires: new Date('2099-12-31'),
                secure: true,
                sameSite: 'lax',
              });
              // otherwise just use localStorage
            } else {
              localStorage.setItem('token', res.key);
            }
          }
          // get user info
          dispatch(getUserData()).then(() => {
            const next = query.next as string;
            if (next === '/') {
              push('/feed');
            } else if (!next?.startsWith('/user')) {
              push(next || '/feed');
            } else {
              push('/feed');
            }
          });
        });
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      {/* PASSWORD */}
      <FormControl className={classes.inputfieldFullWidth} variant="outlined">
        <InputLabel
          color="primary"
          htmlFor="password"
          margin="dense"
          error={!!formik.errors.password}
        >
          {t('login.password')}
        </InputLabel>
        <OutlinedInput
          id="password"
          name="password"
          type={showPassword ? 'text' : 'password'}
          label={t('login.password')}
          onChange={formik.handleChange}
          value={formik.values.password}
          fullWidth
          className={classes.textField}
          autoFocus
          margin="dense"
          color="primary"
          error={!!formik.errors.password}
          endAdornment={(
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={() => setShowPassword((previous) => !previous)}
              >
                {showPassword ? <Visibility /> : <VisibilityOff />}
              </IconButton>
            </InputAdornment>
          )}
        />
      </FormControl>
      {/* Show password errors in the validation */}
      {formik.errors.password && (
        <Alert severity="error" className={classes.errors}>
          {formik.errors.password}
        </Alert>
      )}
      {/* Show if there was a problem in the login */}
      {auth.error && (
        <Alert severity="error" className={classes.errors}>
          {t('login.server_errors.generic')}
        </Alert>
      )}
      {/* SUBMIT BUTTON */}
      <Spacer spacing={3} />
      <Button
        variant="contained"
        fullWidth
        color="primary"
        type="submit"
        className={classes.nextButton}
        endIcon={<ArrowRightAltIcon />}
        disabled={auth.isLoading}
        text={
          !auth.isLoading ? t('login.login') : <CircularProgress size={17} />
        }
      />
    </form>
  );
};

FormPassword.defaultProps = (defaultProps as unknown) as Partial<Props>;

export default FormPassword;
